import React, {useEffect, useRef, useState} from 'react';
import Plotly, {LayoutAxis} from "plotly.js"
import {AxesConfig, DatasetChartProps, DatasetSpecification, PlotDataEx} from "./interfaces";
import {clipboardIcon, forkIcon, statusIcon} from "./icons";
import {defaultFromDateTime, defaultToDateTime} from "./utils";
import {useResizeDetector} from "react-resize-detector";
import Plot from "react-plotly.js";

import {addToDate, getDiffSeconds, truncateDate} from "utils/time-utils";

import {Button} from "primereact/button";
import {ProgressSpinner} from "primereact/progressspinner";
import {SelectButton} from "primereact/selectbutton";
import {Tooltip} from "primereact/tooltip";

import "./DatasetLineChartStyle.scss";


import CustomToggleButton from "../../basic/custom-toggle-button/CustomToggleButton";
import {copyChartToClipBoard} from "./chartUtils";
import {extractNumber} from "../product-template-table/utils/utils";
import DatasetStatusShower from "../../basic/datasetStatusShower/DatasetStatusShower";
import CalendarWrapper from "../../basic/calendar-wrapper/CalendarWrapper";
import {useGetDataInfoRange} from "api/api-hooks/datasets-hooks";
import {DatasetDataInfoResponse} from "../../../api/model/dataset/DatasetDataInfoResponse";


const commonAxisConfig = {
    showspikes: true,
    spikemode: "toaxis",
    automargin: true,
    tickformat: 'f'
} as Partial<LayoutAxis>

const secondaryAxisConfig = {
    ...commonAxisConfig,
    overlaying: 'y',
    showgrid: false
}

const xAxisCommonConfig = {
    spikemode: "toaxis",
    showspikes: true,
}

const layoutConfig = {
    legend: {"orientation": "h"},
    margin: {
        l: 0,
        r: 0,
        b: 30,
        t: 20,
    }
} as Plotly.Layout


export default function DatasetLineChart(props: DatasetChartProps) {
    const identifiers = props.datasets ? props.datasets.map((ds) => ds.identifier) : props.identifiers;

    const {width, height, ref} = useResizeDetector();
    const {height: height2, ref: ref2} = useResizeDetector();

    const [axesConfig, setAxesConfig] = useState<AxesConfig>({});
    const [axesGrouped, setAxesGrouped] = useState(true);

    const [dialog, setDialog] = useState(false);
    const [firstData, setFirstData] = useState([])

    const [from, setFrom] = useState(defaultFromDateTime(
        {
            loadConf: {
                alignUnit: "MINUTES",
                lag: 6,
                lagUnit: "HOURS",
                lead: 0,
                leadUnit: "HOURS",
                ...props.loadConf
            },
            referenceTime: props.referenceTime
        }
    ));
    const [to, setTo] = useState(defaultToDateTime(
        {
            loadConf: {
                alignUnit: "MINUTES",
                lag: 6,
                lagUnit: "HOURS",
                lead: 0,
                leadUnit: "HOURS",
                ...props.loadConf
            },
            referenceTime: props.referenceTime
        }
    ));

    const [loaded, setLoaded] = useState(false);

    const [changedTimeOffset, setChangedTimeOffset] = useState<boolean>(false);
    const [selectedTimeOffset, setSelectedTimeOffset] = useState<string | undefined>(undefined);
    const offsetOptions = [
        {label: "H", value: "HOURS"},
        {label: "D", value: "DAYS"},
        {label: "W", value: "WEEKS"},
        {label: "M", value: "MONTHS"}
    ];


    const requestingTime = useRef<{ from: Date | undefined, to: Date | undefined, loading: boolean }>({
        from: undefined,
        to: undefined,
        loading: false
    });

    const [refresh, setRefresh] = useState<{ updateFrequency: number, turned: boolean }>({
        updateFrequency: props.updateFrequency ?? 5,
        turned: !!props.updateFrequency
    })

    const presentDate = useRef<Date>();

    const {data: traces, isFetching: isLoading, refetch,} = useGetDataInfoRange(identifiers as string[],
        {
            timeStart: from.toISOString(),
            timeEnd: to.toISOString(),
            usePromiseHandler: (res) => {
                requestingTime.current.to = new Date()
                requestingTime.current.loading = false
                if (!res.error) {
                    setFirstData(res.data);
                    return res.data.map((ds: DatasetDataInfoResponse) => {
                        const x: Date[] = []
                        const y: number[] = []

                        ds.data.forEach((v) => {
                            x.push(new Date(v.key as string));
                            y.push(v.value)
                        });

                        return {
                            identifier: ds.identifier,
                            name: ds.title,
                            type: "scatter",
                            mode: "lines",
                            unit: ds.unit,
                            showlegend: true,
                            hovertemplate: `<b>%{y:.3f}</b> at %{x} <br>${ds.title}<extra></extra>`,
                            x: x,
                            y: y,
                        } as PlotDataEx
                    });
                } else {
                    console.log(res.error.message)
                    throw res.error;
                }
            }
        },
        {
            enabled: from && to && identifiers.length > 0,
            staleTime: 1000
        })

    useEffect(() => {
        if (isLoading && !requestingTime.current.loading) {
            requestingTime.current.from = new Date()
            requestingTime.current.loading = true
        }
    }, [isLoading])


    useEffect(() => {
        if (refresh.turned && refresh.updateFrequency && refresh.updateFrequency > 0 && presentDate && presentDate.current) {
            const interval = setInterval(() => {
                let tempDate = truncateDate(new Date(), "MINUTES");
                if (presentDate && presentDate.current &&
                    presentDate.current.toISOString().localeCompare(truncateDate(new Date(), "MINUTES").toISOString()) !== 0) {
                    let minutesToAdd = getDiffSeconds(tempDate, presentDate.current) / 60;
                    if (minutesToAdd > 0) {
                        addTimeToBothDates(minutesToAdd)
                        presentDate.current = truncateDate(new Date(), "MINUTES");
                    }
                } else {
                    refetch();
                }

            }, refresh.updateFrequency * 1000);
            return () => clearInterval(interval);
        }
    }, [refresh.turned, refresh.updateFrequency, presentDate.current])

    useEffect(() => {
        if (traces && traces.length > 0) {
            if (axesGrouped) {
                groupByUnitTraces(traces)
            } else {
                ungroupTraces(traces)
            }

            setLoaded(true)
        }
    }, [traces, axesGrouped, width]);

    useEffect(() => {
        if (selectedTimeOffset) {
            setFrom(addToDate(new Date(to), -1, selectedTimeOffset));
        } else {
            if (from && changedTimeOffset) {
                setFrom(defaultFromDateTime({loadConf: props.loadConf, referenceTime: props.referenceTime}));
            }
        }
    }, [selectedTimeOffset])

    const addTimeToBothDates = (minutesToAdd: number) => {
        setFrom(oldFrom => addToDate(oldFrom, minutesToAdd, "MINUTES"));
        setTo(oldTo => addToDate(oldTo, minutesToAdd, "MINUTES"));
    };

    const onRefresh = () => {
        if (from && to && identifiers) {
            refetch();
        }
    }

    const toggleGroupingHandler = () => {
        setAxesGrouped(axesGrouped => !axesGrouped);
    }

    const showStatus = () => {
        setDialog(true)
    }

    const setFromDateHandler = (date: Date) => {
        if (refresh.turned) {
            setRefresh({...refresh, turned: false})
        }
        setChangedTimeOffset(false);
        setFrom(date)
        setSelectedTimeOffset(undefined);
    }

    const setToDateHandler = (date: Date) => {
        if (refresh.turned) {
            setRefresh({...refresh, turned: false})
        }
        setChangedTimeOffset(false);
        setTo(date)
        setSelectedTimeOffset(undefined);
    }

    const onSelectButtonChange = (e: any) => {
        if (refresh.turned) {
            setRefresh({...refresh, turned: false})
        }
        setSelectedTimeOffset(e.value);
        setChangedTimeOffset(true);
    }

    const syncPresentData = () => {
        presentDate.current = truncateDate(new Date(), "MINUTES")
    }

    const groupByUnitTraces = (data: Array<Partial<PlotDataEx>>) => {
        const unitMap = new Map<string, string>()
        const axes = axesConfig
        const domain = [0, 1]

        for (let i = 0; i < data.length; i++) {
            const unit = data[i].unit!!;
            const trace = data[i];

            const range = getRange(data[i].identifier, props.datasets)

            if (!unitMap.has(unit)) {
                const axCount = unitMap.size + 1;

                if (axCount == 1) {
                    unitMap.set(unit, 'y')
                    axes.yaxis = createAxisConfig(axCount, unit!, domain,range);
                } else {
                    unitMap.set(unit, `y${axCount}`)

                    axes[`yaxis${axCount}`] = createAxisConfig(axCount, unit!, domain,range)
                }
            }

            trace.yaxis = unitMap.get(unit)!!;
        }

        axes.xaxis = {...xAxisCommonConfig, domain: domain} as LayoutAxis

        setAxesConfig({...axes})
    }

    const ungroupTraces = (data: Array<Partial<PlotDataEx>>) => {
        const axes = axesConfig
        const domain = [0, 1]

        for (let i = 0; i < data.length; i++) {
            const unit = data[i].unit;
            const trace = data[i];
            const axis = i + 1
            const range = getRange(data[i].identifier, props.datasets)


            if (axis === 1) {
                axes.yaxis = createAxisConfig(axis, unit!, domain,range)
            } else {
                axes[`yaxis${axis}`] = createAxisConfig(axis, unit!, domain,range)
                trace.yaxis = `y${axis}`
            }
        }

        axes.xaxis = {...xAxisCommonConfig, domain: domain} as LayoutAxis

        setAxesConfig({...axes})
    }

    const createAxisConfig = (axis: number, unit: string, domain: Array<number>, range?: Array<number>): Plotly.LayoutAxis => {
        if (axis === 1) {
            return {
                title: {
                    text: unit,
                    standoff: 10
                },
                range:range,
                ...commonAxisConfig,
            } as LayoutAxis
        } else if (axis === 2) {
            return {
                title: {
                    text: unit,
                    standoff: 10
                },
                side: 'right',
                anchor: 'x',
                range:range,
                ...secondaryAxisConfig,
            } as LayoutAxis
        } else {
            let axOffset;
            const pxVal = 100 / width!!;
            const offVal = pxVal * 0.75;

            // Left
            if (axis % 2 == 0) {
                axOffset = ((axis / 2 | 0) * offVal) - offVal * 2;
                domain[0] = Math.max(0, axOffset) + offVal
            }
            // Right
            else {
                axOffset = (1 - (axis / 2 | 0) * offVal) + offVal

                if (axOffset > 0) {
                    domain[1] = axOffset - offVal - offVal / 3;
                }
            }

            return {
                title: {
                    text: unit,
                    standoff: 4
                },
                position: axOffset,
                side: 'left',
                anchor: 'free',
                range:range,
                ...secondaryAxisConfig
            } as LayoutAxis
        }
    }

    const getRange = (identifier?: string, datasets?:DatasetSpecification[]) => {
        if(!datasets || !identifier) return undefined;

        return datasets.find((d:DatasetSpecification) => d.identifier === identifier)?.range
    }

    const getChartHeight = () => {
        if (props.height != null) {
            return extractNumber(props.height)
        } else if (height && height2 && height >= 0 && height2 >= 0 && props.dynamicHeight) {
            return height - height2 - 4
        } else {
            return 400;
        }
    }

    const getChartWidth = () => {
        if (props.width != null) {
            return extractNumber(props.width)
        } else {
            return width;
        }
    }

    const totalDataPoints = traces ? traces.map((t: any) => t.x?.length).reduce((c: any, a: any) => c! + a!, 0) : 0;

    const chartConfig: Partial<Plotly.Config> = {
        displaylogo: false,
        displayModeBar: props.reportMode ? false : "hover",
        modeBarButtonsToRemove: ["lasso2d", "select2d"],
        modeBarButtonsToAdd: [
            {
                title: 'Toggle trace grouping',
                name: 'Toggle trace grouping',
                icon: forkIcon,
                click: toggleGroupingHandler
            },
            {
                title: 'Copy chart to Clipboard',
                name: 'Copy chart to Clipboard',
                icon: clipboardIcon,
                click: copyChartToClipBoard
            },
            {
                title: 'Show Statistics',
                name: 'Show Statistics',
                icon: statusIcon,
                click: showStatus
            }
        ],

    }

    const printRequestingTime = () => {
        if (requestingTime.current.from && requestingTime.current.to) {
            let time = requestingTime.current.to.getTime() - requestingTime.current.from.getTime();
            if (time > 1000) {
                return (time / 1000).toFixed(1) + "s";
            } else {
                return time.toFixed(0) + "ms";
            }
        } else {
            return "-"
        }
    }

    return <div className="dataset-line-chart" ref={ref} style={{overflow: "hidden", height: `100%`}}>
        {loaded &&
            // Date selection / chart header
            <div ref={ref2} className="flex align-items-center p-mx-2 justify-content-between non-printable">
                <div className="flex align-items-center p-mx-2 ">
                    {props.showDateSelection &&
                        <>
                            <CalendarWrapper id="from"
                                             value={from}
                                             onChange={setFromDateHandler}
                                             className="p-mr-2"
                                             debounceTimeInMs={300}
                            />
                            <CalendarWrapper id="to"
                                             value={to}
                                             onChange={setToDateHandler}
                                             className="p-mr-2"
                                             debounceTimeInMs={300}
                            />
                        </>
                    }

                    <div className="flex gap-2 align-items-center">
                        <div style={{flexWrap: "nowrap", whiteSpace: "nowrap"}}>
                            <SelectButton value={selectedTimeOffset}
                                          options={offsetOptions}
                                          onChange={onSelectButtonChange}
                                          className={"select-button-class"}
                            />
                        </div>

                        <span className="flex align-items-center">Total data points:
                            {isLoading ?
                                <span><ProgressSpinner style={{width: '2.5rem', height: '2.5rem'}} strokeWidth="4"
                                                       fill="var(--surface-ground)" animationDuration=".5s"/></span>
                                :
                                <>
                                    <Tooltip target={".time-spent-requesting"} showDelay={1000}/>
                                    <span className="time-spent-requesting"
                                          data-pr-tooltip={printRequestingTime()}>
                                    {totalDataPoints}
                                </span>
                                </>
                            }
                    </span>
                    </div>
                </div>
                <div className={"flex align-items-center"}>
                    {!props.hideRefresh &&
                        <div>
                            <Button icon="pi pi-refresh" type="button" loading={isLoading}
                                    onClick={onRefresh}
                                    className="p-mr-2 p-button-sm"
                            />
                        </div>
                    }

                    <CustomToggleButton active={refresh.turned}
                                        setActive={(val) => {
                                            syncPresentData()
                                            setRefresh({...refresh, turned: val})
                                        }}
                    />
                </div>
            </div>
        }

        {!loaded &&
            // Progress spinner
            <div className="flex justify-content-center p-mb-4 non-printable">
                <ProgressSpinner style={{width: '20rem', height: '20rem'}} strokeWidth="4"
                                 animationDuration="1s"/>
            </div>
        }

        {loaded && <Plot
            className="ea-plotly-chart"
            config={{...chartConfig}}
            data={traces || []}
            layout={{
                ...axesConfig,
                ...layoutConfig,
                width: getChartWidth(),
                height: getChartHeight(),
            }}
        />}
        {dialog &&
            <DatasetStatusShower dialog={dialog} setDialog={setDialog} datasets={firstData}/>
        }
    </div>
}